import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'

const DEVICE_ID_KEY = 'web_device_id'
const REFRESH_LOCK_KEY = 'fastpro_auth_refresh_lock'
const REFRESH_CHANNEL = 'fastpro_auth_session'
const SESSION_REVOKED_EVENT_KEY = 'fastpro_auth_session_revoked'
const LOCK_TIMEOUT_MS = 15000
export const AUTH_API_TIMEOUT_MS = 15000
export const FIREBASE_AUTH_TIMEOUT_MS = 12000

let activeRefreshPromise = null
let firebaseAuthReadyPromise = null
let refreshChannel = null
const logoutListeners = []
const sessionRevokedListeners = []
let lastSessionRevokedEventId = null

function createTimeoutError (message) {
	const error = new Error(message)
	error.code = 'AUTH_TIMEOUT'
	return error
}

function createAuthError (code, message) {
	const error = new Error(message)
	error.code = code
	return error
}

export function withAuthTimeout (promise, timeoutMs, message = 'Quá thời gian chờ xác thực.') {
	let timer = null
	const timeout = new Promise((_, reject) => {
		timer = setTimeout(() => reject(createTimeoutError(message)), timeoutMs)
	})

	return Promise.race([Promise.resolve(promise), timeout]).finally(() => {
		if (timer) clearTimeout(timer)
	})
}

function waitForFirebaseAuthUser () {
	const auth = firebase.auth()
	if (auth.currentUser) return Promise.resolve(auth.currentUser)

	if (!firebaseAuthReadyPromise) {
		firebaseAuthReadyPromise = withAuthTimeout(
			new Promise((resolve, reject) => {
				let unsubscribe = () => {}
				unsubscribe = auth.onAuthStateChanged(
					(user) => {
						unsubscribe()
						resolve(user)
					},
					(error) => {
						unsubscribe()
						reject(error)
					}
				)
			}),
			FIREBASE_AUTH_TIMEOUT_MS,
			'Firebase không phản hồi khi khôi phục phiên đăng nhập.'
		).finally(() => {
			firebaseAuthReadyPromise = null
		})
	}

	return firebaseAuthReadyPromise
}

function notifyLogoutListeners () {
	logoutListeners.forEach((listener) => listener())
}

function notifySessionRevokedListeners (message, eventId) {
	if (eventId && eventId === lastSessionRevokedEventId) return
	lastSessionRevokedEventId = eventId || null
	sessionRevokedListeners.forEach((listener) => listener(message))
}

function publishSessionRevoked (error) {
	const message = error?.response?.data?.error?.message ||
		'Tài khoản đã được đăng nhập ở cửa sổ ẩn danh hoặc trên thiết bị/nơi khác. Vui lòng đăng nhập lại.'
	const event = {
		event_id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
		message
	}

	notifySessionRevokedListeners(event.message, event.event_id)
	if (refreshChannel) {
		try {
			refreshChannel.postMessage({ type: 'session-revoked', ...event })
		} catch (_) {}
	}
	try {
		localStorage.setItem(SESSION_REVOKED_EVENT_KEY, JSON.stringify(event))
	} catch (_) {}
}

if (typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined') {
	refreshChannel = new window.BroadcastChannel(REFRESH_CHANNEL)
	refreshChannel.onmessage = ({ data }) => {
		if (data && data.type === 'token-updated' && data.token) {
			localStorage.setItem('token', data.token)
		} else if (data && data.type === 'session-revoked') {
			notifySessionRevokedListeners(data.message, data.event_id)
		} else if (data && data.type === 'logout') {
			notifyLogoutListeners()
		}
	}
}

if (typeof window !== 'undefined') {
	window.addEventListener('storage', (event) => {
		if (event.key === SESSION_REVOKED_EVENT_KEY && event.newValue) {
			try {
				const payload = JSON.parse(event.newValue)
				notifySessionRevokedListeners(payload.message, payload.event_id)
			} catch (_) {}
		}
		if (
			(event.key === 'fastpro_auth_logout_at' && event.newValue) ||
			(event.key === 'token' && event.oldValue && !event.newValue)
		) {
			notifyLogoutListeners()
		}
	})
}

function createDeviceId () {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID()
	}

	return `web-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

export function getDeviceId () {
	let deviceId = localStorage.getItem(DEVICE_ID_KEY)
	if (!deviceId) {
		deviceId = createDeviceId()
		localStorage.setItem(DEVICE_ID_KEY, deviceId)
	}
	return deviceId
}

export function getDeviceMetadata () {
	const userAgent = navigator.userAgent || ''
	const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || 'web'
	let deviceType = 'desktop'

	if (/ipad|tablet|playbook|silk/i.test(userAgent)) {
		deviceType = 'tablet'
	} else if (/android|iphone|ipod|blackberry|iemobile|opera mini|mobile/i.test(userAgent)) {
		deviceType = 'mobile'
	}

	return {
		device_identifier: getDeviceId(),
		device_type: deviceType,
		device_name: String(platform).slice(0, 255)
	}
}

function waitForSharedToken (staleToken, timeoutMs = LOCK_TIMEOUT_MS) {
	return new Promise((resolve) => {
		let completed = false
		const finish = (token) => {
			if (completed) return
			completed = true
			window.removeEventListener('storage', onStorage)
			clearTimeout(timer)
			clearInterval(pollTimer)
			resolve(token)
		}
		const onStorage = (event) => {
			if (event.key === 'token' && event.newValue && event.newValue !== staleToken) {
				finish(event.newValue)
			}
		}
		const pollTimer = setInterval(() => {
			const token = localStorage.getItem('token')
			if (token && token !== staleToken) finish(token)
		}, 100)
		const timer = setTimeout(() => finish(null), timeoutMs)
		window.addEventListener('storage', onStorage)
	})
}

async function refreshOnBackend (refreshUrl, staleToken) {
	const sharedToken = localStorage.getItem('token')
	if (sharedToken && staleToken && sharedToken !== staleToken) {
		return sharedToken
	}

	const user = await waitForFirebaseAuthUser()
	if (!user) {
		throw createAuthError('AUTH_NO_USER', 'Firebase session is not authenticated')
	}

	const idToken = await withAuthTimeout(
		user.getIdToken(true),
		FIREBASE_AUTH_TIMEOUT_MS,
		'Firebase không phản hồi khi làm mới phiên đăng nhập.'
	)
	try {
		await axios.create({ timeout: AUTH_API_TIMEOUT_MS }).post(refreshUrl, {
			email: localStorage.getItem('email'),
			token: idToken,
			...getDeviceMetadata()
		})
	} catch (error) {
		if (isSessionRevokedError(error)) publishSessionRevoked(error)
		throw error
	}

	localStorage.setItem('token', idToken)
	if (refreshChannel) {
		refreshChannel.postMessage({ type: 'token-updated', token: idToken })
	}

	return idToken
}

async function refreshWithNavigatorLock (refreshUrl, staleToken) {
	const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
	const timer = controller ? setTimeout(() => controller.abort(), LOCK_TIMEOUT_MS) : null

	try {
		if (!controller) {
			return await navigator.locks.request(
				REFRESH_CHANNEL,
				() => refreshOnBackend(refreshUrl, staleToken)
			)
		}
		return await navigator.locks.request(
			REFRESH_CHANNEL,
			{ signal: controller.signal },
			() => refreshOnBackend(refreshUrl, staleToken)
		)
	} catch (error) {
		if (error && error.name === 'AbortError') {
			return refreshWithStorageLock(refreshUrl, staleToken)
		}
		throw error
	} finally {
		if (timer) clearTimeout(timer)
	}
}

async function refreshWithStorageLock (refreshUrl, staleToken) {
	const owner = createDeviceId()
	const now = Date.now()
	let lock = null

	try {
		lock = JSON.parse(localStorage.getItem(REFRESH_LOCK_KEY) || 'null')
	} catch (_) {
		lock = null
	}

	if (lock && lock.expires_at > now) {
		const sharedToken = await waitForSharedToken(staleToken)
		if (sharedToken) return sharedToken
	}

	localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ owner, expires_at: Date.now() + LOCK_TIMEOUT_MS }))
	try {
		return await refreshOnBackend(refreshUrl, staleToken)
	} finally {
		try {
			const currentLock = JSON.parse(localStorage.getItem(REFRESH_LOCK_KEY) || 'null')
			if (currentLock && currentLock.owner === owner) {
				localStorage.removeItem(REFRESH_LOCK_KEY)
			}
		} catch (_) {
			localStorage.removeItem(REFRESH_LOCK_KEY)
		}
	}
}

export function refreshAuthToken (refreshUrl, staleToken = localStorage.getItem('token')) {
	if (activeRefreshPromise) return activeRefreshPromise

	const runRefresh = async () => {
		if (navigator.locks && typeof navigator.locks.request === 'function') {
			return refreshWithNavigatorLock(refreshUrl, staleToken)
		}
		return refreshWithStorageLock(refreshUrl, staleToken)
	}

	activeRefreshPromise = runRefresh().finally(() => {
		activeRefreshPromise = null
	})

	return activeRefreshPromise
}

export function isTerminalRefreshError (error) {
	return Boolean(
		error && (
			error.code === 'AUTH_NO_USER' ||
			(error.response && [401, 403].includes(error.response.status))
		)
	)
}

export function isSessionRevokedError (error) {
	return error?.response?.data?.error?.code === 'SESSION_REVOKED'
}

export function broadcastAuthLogout () {
	if (refreshChannel) {
		refreshChannel.postMessage({ type: 'logout' })
	}
	localStorage.setItem('fastpro_auth_logout_at', String(Date.now()))
}

export function onAuthSessionLogout (listener) {
	if (typeof listener === 'function') {
		logoutListeners.push(listener)
	}
}

export function onAuthSessionRevoked (listener) {
	if (typeof listener === 'function') {
		sessionRevokedListeners.push(listener)
	}
}
